import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { RiDashboardLine, RiDiscountPercentFill } from "react-icons/ri";
import { IoClose } from "react-icons/io5";
import { SiHackthebox } from "react-icons/si";
import { TbCategory2 } from "react-icons/tb";
import { MdOutlineCategory, MdOutlineInventory, MdDeliveryDining } from "react-icons/md";
import { PiNotepad } from "react-icons/pi";
import { PiUsersThree } from "react-icons/pi";
import { FaEnvelope, FaQuestionCircle } from "react-icons/fa";
import { PiFlagBannerFold } from "react-icons/pi";
import { BiMessageAltDetail } from "react-icons/bi";
import { FiHome, FiChevronDown } from "react-icons/fi";
import { LiaSellcast } from "react-icons/lia";

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({
    Categories: pathname.includes('/admin-categories') || pathname.includes('/admin-sub-categories'),
    Inventory: pathname.includes('/admin-inventory'),
    Reports: pathname.startsWith('/admin-reports'),
    "Shipping & Delivery": pathname.includes('/admin-shipping') || pathname.includes('/admin-delivery-zones'),
  });

  useEffect(() => {
    if (pathname.startsWith('/admin-reports')) {
      setExpandedMenus((prev) => ({ ...prev, Reports: true }));
    }
  }, [pathname]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleSubMenu = (label, e) => {
    e.stopPropagation();
    setExpandedMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  // Menu items configuration
  const menuItems = [
    {
      label: "Dashboard",
      icon: <RiDashboardLine size={20} />,
      path: "/dashboard",
    },
    {
      label: "Orders",
      icon: <PiNotepad size={20} />,
      path: "/admin-orders",
    },
    {
      label: "Products",
      icon: <SiHackthebox size={20} />,
      path: "/admin-products",
    },
    {
      label: "Categories",
      icon: <TbCategory2 size={20} />,
      path: "/admin-categories",
      subItems: [
        { label: "All Categories", path: "/admin-categories" },
        { label: "Subcategories", path: "/admin-sub-categories" },
      ],
    },
    {
      label: "Sellers / Artisans",
      icon: <LiaSellcast size={20} />,
      path: "/admin-sellers",
    },
    {
      label: "Customers",
      icon: <PiUsersThree size={20} />,
      path: "/admin-customers",
    },
    {
      label: "Inventory",
      icon: <MdOutlineInventory size={20} />,
      path: "/admin-inventory",
      subItems: [
        { label: "Inventory Dashboard", path: "/admin-inventory/dashboard" },
        { label: "All Inventory", path: "/admin-inventory/all-inventory" },
        { label: "Admin Stock", path: "/admin-inventory/admin-stock" },
        { label: "Vendor Stock", path: "/admin-inventory/vendor-stock" },
        { label: "Warehouse", path: "/admin-inventory/warehouse" },
        { label: "Stock Movement", path: "/admin-inventory/stock-movement" },
        { label: "Stock Adjustment", path: "/admin-inventory/stock-adjustment" },
        { label: "Damage & Loss", path: "/admin-inventory/damage-loss" },
        { label: "Repair Management", path: "/admin-inventory/repair-management" },
        { label: "Returns / Replacement", path: "/admin-inventory/returns-replacement" },
        { label: "Stock Transfer", path: "/admin-inventory/stock-transfer" },
        { label: "Stock Count & Recon", path: "/admin-inventory/stock-count" },
        { label: "Reports & Export", path: "/admin-inventory/reports" },
      ],
    },
    {
      label: "Purchase & Stock",
      icon: <TbCategory2 size={20} />,
      path: "/admin-purchase",
    },
    {
      label: "Returns & Refunds",
      icon: <FaQuestionCircle size={20} />,
      path: "/admin-returns",
    },
    {
      label: "Shipping & Delivery",
      icon: <MdOutlineCategory size={20} />,
      path: "/admin-shipping",
      subItems: [
        {
          label: "Delivery Zones",
          path: "/admin-delivery-zones",
          icon: <MdDeliveryDining size={16} />,
        },
      ],
    },
    {
      label: "Payments & Settlement",
      icon: <RiDiscountPercentFill size={20} />,
      path: "/admin-payments",
    },
    {
      label: "Discounts & Coupons",
      icon: <RiDiscountPercentFill size={20} />,
      path: "/admin-discount",
    },
    {
      label: "Reports",
      icon: <PiNotepad size={20} />,
      path: "/admin-reports",
      subItems: [
        { label: "Reports Dashboard", path: "/admin-reports" },
        { label: "Sales Report", path: "/admin-reports/sales" },
        { label: "Order Report", path: "/admin-reports/orders" },
        { label: "Product Report", path: "/admin-reports/products" },
        { label: "Seller Report", path: "/admin-reports/sellers" },
        { label: "Customer Report", path: "/admin-reports/customers" },
        { label: "Inventory Report", path: "/admin-reports/inventory" },
        { label: "Purchase Report", path: "/admin-reports/purchase" },
        { label: "Payment & Settlement", path: "/admin-reports/payments" },
        { label: "Refunds Report", path: "/admin-reports/refunds" },
        { label: "Tax Report (GST)", path: "/admin-reports/tax" },
        { label: "Marketing Report", path: "/admin-reports/marketing" },
        { label: "Shipping Report", path: "/admin-reports/shipping" },
        { label: "Custom Report", path: "/admin-reports/custom" },
      ],
    },
    {
      label: "Artisans & Crafts",
      icon: <LiaSellcast size={20} />,
      path: "/admin-artisans",
    },
    {
      label: "Content & Pages",
      icon: <PiFlagBannerFold size={20} />,
      path: "/app-banners",
    },
    {
      label: "Marketing",
      icon: <BiMessageAltDetail size={20} />,
      path: "/admin-marketing",
    },
    {
      label: "Notifications",
      icon: <FaEnvelope size={20} />,
      path: "/admin-notifications",
    },
    {
      label: "Support / Tickets",
      icon: <FaQuestionCircle size={20} />,
      path: "/admin-support",
    },
    {
      label: "Employees & Roles",
      icon: <PiUsersThree size={20} />,
      path: "/admin-employees",
    },
    {
      label: "Settings",
      icon: <MdOutlineCategory size={20} />,
      path: "/admin-settings",
    },
  ];

  const renderMenuItems = (isMobile = false) => (
    <ul className="space-y-1 mt-3">
      {menuItems.map((item, index) => {
        const hasSubItems = item.subItems && item.subItems.length > 0;
        const isExpanded = expandedMenus[item.label];
        const isActive =
          pathname === item.path ||
          (hasSubItems && item.subItems.some((sub) => pathname.includes(sub.path)));

        return (
          <motion.li
            key={item.label}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.1 }}
            className="group"
          >
            <div
              onClick={(e) => {
                if (hasSubItems) {
                  toggleSubMenu(item.label, e);
                  if (item.label === "Reports" && !pathname.startsWith('/admin-reports')) {
                    navigate(item.path);
                  }
                } else {
                  navigate(item.path);
                  if (window.innerWidth < 1024) {
                    setIsSidebarOpen(false);
                  }
                }
              }}
              className={`flex items-center justify-between w-full p-3 text-sm tracking-wide rounded-xl transition-all duration-300 cursor-pointer ${isActive
                ? "bg-gradient-to-r from-[#c59a3f] via-[#e5c158] to-[#d4af37] text-black shadow-lg shadow-[#d4af37]/30 border border-[#f3d57b]"
                : "text-gray-600 hover:bg-gray-50/10 hover:text-white dark:text-gray-300 dark:hover:bg-gray-700/50"
                } ${isMobile ? "bg-white/5" : ""}`}
            >
              <div className="flex items-center">
                <span
                  className={`transition-all duration-300 ${isActive
                    ? "text-black scale-110"
                    : "text-gray-400 group-hover:text-white group-hover:scale-110"
                    }`}
                >
                  {item.icon}
                </span>
                <span
                  className={`ml-3 font-semibold tracking-wider transition-all duration-300 ${isActive ? "text-black" : "text-gray-300 group-hover:text-white font-normal"
                    }`}
                >
                  {item.label}
                </span>
              </div>
              {hasSubItems && (
                <span
                  className={`transition-transform duration-300 ${isExpanded ? "rotate-180" : ""
                    } ${isActive ? "text-black" : "text-gray-400 group-hover:text-white"}`}
                >
                  <FiChevronDown size={16} />
                </span>
              )}
              {isActive && !hasSubItems && (
                <motion.span
                  className="ml-auto h-2 w-2 bg-black rounded-full shadow-[0_0_8px_rgba(0,0,0,0.5)]"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 15 }}
                />
              )}
            </div>

            {hasSubItems && (
              <AnimatePresence>
                {isExpanded && (
                  <motion.ul
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden ml-4 mt-2 space-y-1 border-l border-gray-600/50 pl-2 relative"
                  >
                    {item.subItems.map((subItem) => (
                      <li key={subItem.path}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(subItem.path);
                            if (window.innerWidth < 1024) {
                              setIsSidebarOpen(false);
                            }
                          }}
                          className={`flex items-center w-full p-2 text-sm tracking-wide rounded-lg transition-all duration-300 relative ${pathname === subItem.path
                            ? "text-[#d4af37] bg-[#d4af37]/15 font-semibold shadow-xs"
                            : "text-gray-400 hover:text-white hover:bg-white/5"
                            }`}
                        >
                          {pathname === subItem.path && (
                            <motion.div
                              layoutId={`activeSubMenu-${item.label}`}
                              className="absolute left-[-9px] w-2 h-2 rounded-full bg-[#d4af37] shadow-[0_0_8px_rgba(212,175,55,0.8)]"
                            />
                          )}
                          {pathname === subItem.path ? (
                            <span className="text-[#d4af37] text-[10px] mr-1.5 font-bold">▶</span>
                          ) : (
                            <span className="w-2.5 inline-block" />
                          )}
                          <span className="ml-1">{subItem.label}</span>
                        </button>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            )}
          </motion.li>
        );
      })}
    </ul>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <motion.div
          className="flex flex-col w-64 xl:w-72 h-screen overflow-y-auto overflow-x-hidden bg-[#131921] relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#fba93d]/10 rounded-full -translate-y-16 translate-x-16 blur-2xl"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#014470]/30 rounded-full translate-y-12 -translate-x-12 blur-xl"></div>

          {/* Header */}
          {/* <div className="relative py-3.5 px-6 bg-[#131921] border-b border-white/10 shadow-lg">
            <div className="flex items-center gap-3">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => navigate("/")}
                className="p-1.5 bg-white/10 rounded-xl hover:bg-white/20 cursor-pointer transition-all duration-300 backdrop-blur-sm border border-white/10"
              >
                <FiHome size={16} className="text-white" />
              </motion.div>
              <div>
                <motion.h2
                  className="text-2xl font-semibold text-white tracking-wide"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  MENU
                </motion.h2>
              </div>
            </div>
          </div> */}

          {/* Navigation Items */}
          <div className="relative px-4 py-2 flex-1">
            <div className="relative">{renderMenuItems()}</div>
          </div>
        </motion.div>
      </div>

      {/* Mobile Sidebar */}
      <div className="block lg:hidden">
        {/* Overlay */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
              onClick={toggleSidebar}
            />
          )}
        </AnimatePresence>

        {/* Sidebar Content */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              className="fixed inset-y-0 left-0 z-50 w-80 h-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col w-full h-full overflow-y-auto bg-gradient-to-br from-[#00395F] via-[#014470] to-[#001F36] relative">
                {/* Decorative Elements for Mobile */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-[#fba93d]/10 rounded-full -translate-y-20 translate-x-20 blur-2xl"></div>

                {/* Header */}
                <div className="relative py-6 px-6 bg-gradient-to-r from-[#073a5c] to-[#08527a] border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        onClick={() => navigate("/")}
                        className="p-2 bg-white/10 rounded-xl hover:bg-white/20 cursor-pointer transition-all duration-300"
                      >
                        <FiHome size={20} className="text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold font-philosopher text-white tracking-wide">
                          ADMIN MENU
                        </h2>
                        <p className="text-xs text-white/60 mt-1">
                          Navigation Panel
                        </p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={toggleSidebar}
                      className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-300 backdrop-blur-sm border border-white/10"
                    >
                      <IoClose size={20} className="text-white" />
                    </motion.button>
                  </div>
                </div>

                {/* Navigation Items */}
                <div className="relative px-4 py-6 flex-1">
                  {renderMenuItems(true)}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default Sidebar;
